import { useQuery } from '@tanstack/react-query';

import { apiGet } from '@/api/client';
import { endpoints } from '@/api/endpoints';
import { Card, SectionHeader } from '@/components/ui/Card';
import { Money } from '@/components/ui/Money';
import { PageHeader } from '@/components/ui/PageHeader';
import { StatCard } from '@/components/ui/StatCard';
import { ErrorState, LoadingState } from '@/components/ui/States';

interface BranchValue {
  branch_id: number;
  branch_name?: string;
  cost_value: number;
  selling_value: number;
  units: number;
}

interface InventoryReport {
  cost_value: number;
  selling_value: number;
  potential_margin: number;
  units: number;
  by_branch?: BranchValue[];
}

/** What's sitting on your shelves, valued at cost AND at selling price.
 *  The delta is your potential margin if you sold everything tomorrow. */
export function InventoryValuePage() {
  const query = useQuery({
    queryKey: ['report', 'inventory-value'],
    queryFn: () => apiGet<InventoryReport>(endpoints.reportInventoryValue),
  });

  if (query.isLoading) return <LoadingState />;
  if (query.isError || !query.data) {
    return (
      <ErrorState
        title="Couldn't load inventory value"
        description={(query.error as Error)?.message}
        onRetry={() => query.refetch()}
      />
    );
  }
  const r = query.data;

  return (
    <>
      <PageHeader
        back
        title="Inventory value"
        description="What you've got on the shelves, in rupees."
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
        <StatCard label="Cost value" value={<Money value={r.cost_value} />} />
        <StatCard label="Selling value" value={<Money value={r.selling_value} />} />
        <StatCard
          label="Potential margin"
          value={<Money value={r.potential_margin} />}
          deltaTone="success"
          delta="If you sold everything"
        />
        <StatCard label="Units in stock" value={r.units.toLocaleString('en-IN')} />
      </div>

      <Card padded={false}>
        <div className="px-5 py-4 border-b border-line">
          <SectionHeader title="By branch" />
        </div>
        {(r.by_branch ?? []).length === 0 ? (
          <div className="px-5 py-12 text-center text-ink-soft">
            No per-branch breakdown available.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-2">
                <tr className="text-left text-xs uppercase tracking-wide text-ink-soft">
                  <th className="px-5 py-2">Branch</th>
                  <th className="px-5 py-2 text-right">Units</th>
                  <th className="px-5 py-2 text-right">Cost value</th>
                  <th className="px-5 py-2 text-right">Selling value</th>
                </tr>
              </thead>
              <tbody>
                {r.by_branch!.map((b) => (
                  <tr key={b.branch_id} className="border-t border-line">
                    <td className="px-5 py-3 font-medium">
                      {b.branch_name ?? `Branch ${b.branch_id}`}
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums">
                      {b.units.toLocaleString('en-IN')}
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums">
                      <Money value={b.cost_value} />
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums font-semibold">
                      <Money value={b.selling_value} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
